/**
 * Per-project merge for cross-device project sync. PURE - no signals, no
 * storage, no network, so it can be unit-tested and reasoned about on its
 * own.
 *
 * WHY THIS EXISTS (2026-08-31). Sync used to replace the whole project
 * LIST: `publishProjects()` pushed every project as one opaque document
 * and the pull called `replaceAllProjects()`. Two consequences, both of
 * which bit for real:
 *
 *   1. Two devices could never each contribute a project. A desktop
 *      holding A+B and an iPad holding C converged on whichever pushed
 *      last, and the other side's projects were DELETED - not merged,
 *      not prompted, just gone.
 *   2. Because the connect handler pulled BEFORE it pushed, a device
 *      carrying unpushed work always lost to a device that had merely
 *      pushed more recently. The operator hit exactly this: a developed
 *      `NEW BETA TEST v1` plus an `Untitled` on the iPad, against an old
 *      copy of the same project sitting at rev 333 from the desktop.
 *      Reconnecting the iPad would have destroyed the newer work.
 *
 * So the unit of merge is now the PROJECT, not the list. Both sides are
 * unioned by project id; only a genuine same-project conflict needs a
 * winner, and that is decided by `modifiedAt` rather than by which
 * device happened to reach the bridge last.
 *
 * DELETION NEEDS TOMBSTONES, and this is the part that is easy to get
 * wrong. Under a union rule, "absent" is ambiguous: a project missing
 * from the remote might have been deleted there, or might simply never
 * have reached it. Without a record of the deletion, union-only sync
 * resurrects every deleted project the moment a stale device syncs -
 * which is arguably worse than the data loss it replaced, because it
 * never converges. So a delete records a tombstone, tombstones travel in
 * the synced document alongside the projects, and absence is only
 * treated as deletion when a tombstone says so.
 *
 * RESURRECTION BEATS DELETION when the edit is newer: if one device
 * deleted a project at T1 and another EDITED it at T2 > T1, the edit
 * wins and the project survives. Losing deliberate work to a stale
 * delete is the worse of the two failure modes, and it is the one the
 * operator would notice least until it mattered.
 */

import type { Project } from './projectStore.ts';

/** A record that a project was deleted, and when. Travels in the synced
 *  document so other devices can distinguish "deleted here" from "never
 *  seen here". */
export interface Tombstone {
  id: string;
  deletedAt: number;
}

export interface MergeSide {
  projects: Project[];
  tombstones: Tombstone[];
}

export interface MergeResult {
  projects: Project[];
  tombstones: Tombstone[];
}

/** How long a tombstone is kept before it is garbage-collected. A device
 *  that has been offline longer than this will resurrect projects that
 *  were deleted while it was away - unavoidable without a server-side
 *  vector clock, and 60 days is far beyond the "iPad sat in a flight
 *  case between gigs" window this has to survive. */
export const TOMBSTONE_TTL_MS = 60 * 24 * 60 * 60 * 1000;

/**
 * The effective last-modified time of a project.
 *
 * `modifiedAt` is optional because it was added after projects already
 * existed in the wild, and it is deliberately NOT a schema bump (same
 * additive-optional convention as `pedals` and `panicMidiTrigger`).
 * Falling back to `createdAt` means two copies of the same project that
 * both predate the field compare EQUAL - see `mergeProjects` for why
 * that tie must resolve to REMOTE and not to local.
 */
export function modifiedAtOf(p: Project): number {
  return p.modifiedAt ?? p.createdAt ?? 0;
}

/**
 * Why a project that arrived from ANOTHER writer cannot be applied, or null
 * if it can.
 *
 * WHY (2026-09-22, OPBridge S279). OPBridge is about to author a project
 * document of its own, and `applyMergedProjects` walks `pages[].items[]`
 * with no guard. One doc missing `pages` therefore threw inside the sync
 * tick of EVERY device on the account, on EVERY tick, and froze sync until
 * someone deleted the doc by hand - reproduced by
 * `project_sync_merges_across_devices` before this existed.
 *
 * Deliberately checks ONLY what the app dereferences unguarded, never what
 * it merely prefers. A bad `device` falls back in `canvasSize`, and an
 * `activePageId` that matches no page falls back to `pages[0]`, so neither
 * is grounds to refuse a project. The narrower this is, the less a future
 * build's legitimately different shape gets refused by an older one.
 */
export function projectShapeProblem(p: unknown): string | null {
  if (typeof p !== 'object' || p === null) return 'not an object';
  const o = p as { id?: unknown; pages?: unknown };
  if (typeof o.id !== 'string' || o.id === '') return 'no id';
  if (!Array.isArray(o.pages) || o.pages.length === 0) return 'no pages';
  for (const pg of o.pages as unknown[]) {
    if (typeof pg !== 'object' || pg === null) return 'a page is not an object';
    const items = (pg as { items?: unknown }).items;
    if (!Array.isArray(items)) return 'a page has no items list';
    if (items.some((it) => typeof it !== 'object' || it === null)) return 'an item is not an object';
  }
  return null;
}

/** Latest tombstone per id, newest wins. */
function tombstoneMap(...lists: Tombstone[][]): Map<string, number> {
  const out = new Map<string, number>();
  for (const list of lists) {
    for (const t of list) {
      const prev = out.get(t.id);
      if (prev === undefined || t.deletedAt > prev) out.set(t.id, t.deletedAt);
    }
  }
  return out;
}

/**
 * Merge two sides into one project set plus one tombstone set.
 *
 * `now` is passed in rather than read from the clock so the function
 * stays pure and testable.
 *
 * Resolution, per project id:
 *   - present on both  -> higher `modifiedAt` wins; a TIE resolves to
 *     `remote`. That direction is load-bearing, not a coin flip:
 *     tie-keeps-LOCAL does not converge. Two devices holding different
 *     content under the same id with equal (or absent) timestamps would
 *     each keep their own copy and re-push it on every tick, flipping
 *     the document back and forth every 5 seconds forever. Preferring
 *     remote makes the doc the tie-breaker, so both sides land on the
 *     same value within one tick.
 *
 *     This is not a licence to overwrite: it only fires when the two
 *     timestamps are EQUAL, which in practice means both copies predate
 *     `modifiedAt` entirely (legacy projects, falling back to a shared
 *     `createdAt`). Any edit on either device stamps a real timestamp
 *     and takes that project out of tie territory permanently, so the
 *     window is a one-time migration artefact rather than a standing
 *     hazard. Identical content short-circuits earlier via
 *     `sameProjects()`, so an unchanged sync still writes nothing.
 *   - present on one   -> kept, UNLESS the other side carries a
 *     tombstone at or after that copy's `modifiedAt`.
 *   - tombstoned, but a surviving copy is NEWER than the tombstone ->
 *     the project comes back and the tombstone is dropped.
 *
 * Order is local-first (stable for the device doing the merge), with
 * remote-only projects appended in the remote's own order.
 */
export function mergeProjects(local: MergeSide, remote: MergeSide, now: number): MergeResult {
  const localById = new Map(local.projects.map((p) => [p.id, p]));
  const remoteById = new Map(remote.projects.map((p) => [p.id, p]));
  const deaths = tombstoneMap(local.tombstones, remote.tombstones);

  const winners = new Map<string, Project>();

  for (const id of new Set([...localById.keys(), ...remoteById.keys()])) {
    const l = localById.get(id);
    const r = remoteById.get(id);

    // Present on both sides: newest edit wins, ties resolve to remote so
    // the two devices converge instead of each re-pushing its own copy.
    let winner: Project;
    if (l && r) winner = modifiedAtOf(r) >= modifiedAtOf(l) ? r : l;
    else winner = (l ?? r)!;

    // A tombstone only kills a copy that has NOT been edited since the
    // delete. An edit after the delete is a deliberate resurrection.
    const deletedAt = deaths.get(id);
    if (deletedAt !== undefined && modifiedAtOf(winner) <= deletedAt) continue;

    winners.set(id, winner);
  }

  // Local order first, then remote-only ids in the remote's order. Keeps
  // the merging device's list from reshuffling under it on every sync.
  const ordered: Project[] = [];
  for (const p of local.projects) {
    const w = winners.get(p.id);
    if (w) { ordered.push(w); winners.delete(p.id); }
  }
  for (const p of remote.projects) {
    const w = winners.get(p.id);
    if (w) { ordered.push(w); winners.delete(p.id); }
  }

  const survivingIds = new Set(ordered.map((p) => p.id));
  const tombstones: Tombstone[] = [];
  for (const [id, deletedAt] of deaths) {
    // Drop a tombstone once its project has been resurrected by a newer
    // edit, and garbage-collect ones that have aged out.
    if (survivingIds.has(id)) continue;
    if (now - deletedAt > TOMBSTONE_TTL_MS) continue;
    tombstones.push({ id, deletedAt });
  }
  tombstones.sort((a, b) => a.deletedAt - b.deletedAt);

  return { projects: ordered, tombstones };
}

/** Stable content comparison, used to decide whether a merge actually
 *  changed anything before writing local state or pushing a revision.
 *  Compares the ordered id list and each project's content - cheaper and
 *  far less brittle than deep-equality on the whole tree. */
export function sameProjects(a: Project[], b: Project[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i]!;
    const y = b[i]!;
    if (x.id !== y.id) return false;
    if (JSON.stringify(x) !== JSON.stringify(y)) return false;
  }
  return true;
}

export function sameTombstones(a: Tombstone[], b: Tombstone[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i]!.id !== b[i]!.id || a[i]!.deletedAt !== b[i]!.deletedAt) return false;
  }
  return true;
}
